import { GlobalContext } from 'providers/Global';
import { ModalType, SCREEN_MD } from '../../utils/constants';
import { sendHbarToAccount, sendTokenToAccount } from '../../services/TransactionService';
import { buildCustomStrategy } from '../../signing/useStrategy';
import { useSigningConfirmation } from '../../providers/SigningConfirmation';
import { clearKey } from '../../signing/KeyManager';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    Flex,
    Text,
    ModalBody,
    Input,
    Button,
    Box,
    Skeleton,
    Menu as ChakraMenu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';
import { ReactComponent as WalletConnectLogo } from 'assets/walletconnect-logo.svg';
import { ReactComponent as GreenWalletIcon } from 'assets/wallet-icon-green.svg';
import { ReactComponent as InfoIcon } from 'assets/info-icon.svg';
import { fundWallet } from 'api/multisigAPI';
import { getAccountDataByNetwork } from 'api/userAPI';
import { useParams } from 'react-router-dom';
import { useToast } from 'hooks/useToast';
import HashBasedAvatar from 'components/Avatar';
import { getAvatarUID } from 'utils/avatar';
import TokenIcon from 'components/TokenIcon';
import { TokenBalance } from 'types/token';
import { formatTokenBalance } from 'utils/tokens';
import { getWalletErrorMessage } from 'utils/walletErrors';

type SelectedAsset = { type: 'HBAR' } | { type: 'TOKEN'; token: TokenBalance };

interface ConnectedWalletBalances {
    hbar: number; // in whole HBAR
    tokens: Record<string, number>; // token_id → raw balance
}

const FundWalletModal = () => {
    const [amount, setAmount] = useState('');
    const [selectedAsset, setSelectedAsset] = useState<SelectedAsset>({ type: 'HBAR' });
    const [isLoading, setIsLoading] = useState(false);
    const [walletBalances, setWalletBalances] = useState<ConnectedWalletBalances | null>(null);
    const [balanceFetchFailed, setBalanceFetchFailed] = useState(false);
    const { walletId, multisigAccountId } = useParams();
    const { showToast } = useToast();
    const { requestSignConfirmation } = useSigningConfirmation();

    const {
        connection: { accountId, hashconnectConnectorInstance },
        modals: {
            [`${ModalType.FundWalletModal}`]: { open },
        },
        closeModal,
        connectedNetwork,
        currentAccount,
        balance: multisigHbarBalance,
        updateBalance,
        triggerRefresh,
    } = useContext(GlobalContext);

    const isScreenMedium = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });

    const [walletMeta, setWalletMeta] = useState<{ name: string; icon: string | null } | null>(null);
    useEffect(() => {
        if (open && hashconnectConnectorInstance) {
            setWalletMeta(hashconnectConnectorInstance.getConnectedWalletMeta?.() ?? null);
        }
    }, [open, hashconnectConnectorInstance]);

    const associatedTokens = useMemo(
        () => (currentAccount.tokens || []).filter((t) => t.type === 'FUNGIBLE_COMMON'),
        [currentAccount.tokens],
    );

    // Fetch connected wallet balances once when modal opens
    useEffect(() => {
        if (!open || !accountId || !connectedNetwork) {
            return;
        }
        let cancelled = false;
        setWalletBalances(null);
        setBalanceFetchFailed(false);

        getAccountDataByNetwork(accountId, connectedNetwork)
            .then((data) => {
                if (cancelled) return;
                const tokenMap: Record<string, number> = {};
                for (const t of data.balance?.tokens || []) {
                    tokenMap[t.token_id] = t.balance;
                }
                setWalletBalances({
                    hbar: (data.balance?.balance || 0) / 1e8,
                    tokens: tokenMap,
                });
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Failed to fetch connected wallet balances:', err);
                setBalanceFetchFailed(true);
            });

        return () => { cancelled = true; };
    }, [open, accountId, connectedNetwork]);

    // Derive display balances for the selected asset
    const connectedWalletDisplayBalance = useMemo(() => {
        if (!walletBalances) return null;
        if (selectedAsset.type === 'HBAR') {
            return walletBalances.hbar.toFixed(2);
        }
        const raw = walletBalances.tokens[selectedAsset.token.token_id];
        if (raw === undefined) return '0.00';
        return formatTokenBalance({ ...selectedAsset.token, balance: raw });
    }, [walletBalances, selectedAsset]);

    const multisigDisplayBalance = useMemo(() => {
        if (selectedAsset.type === 'HBAR') {
            return multisigHbarBalance.toFixed(2);
        }
        const token = associatedTokens.find((t) => t.token_id === selectedAsset.token.token_id);
        if (!token) return '0.00';
        return formatTokenBalance(token);
    }, [selectedAsset, multisigHbarBalance, associatedTokens]);

    const handleAssetChange = (value: string) => {
        setAmount('');
        if (value === 'HBAR') {
            setSelectedAsset({ type: 'HBAR' });
        } else {
            const token = associatedTokens.find((t) => t.token_id === value);
            if (token) setSelectedAsset({ type: 'TOKEN', token });
        }
    };

    const maxDecimals = selectedAsset.type === 'HBAR' ? 8 : selectedAsset.token.decimals;

    // Compute the connected wallet's available balance for the selected asset (in human-readable units)
    const availableBalance = useMemo(() => {
        if (!walletBalances) return null;
        if (selectedAsset.type === 'HBAR') return walletBalances.hbar;
        const raw = walletBalances.tokens[selectedAsset.token.token_id];
        if (raw === undefined) return 0;
        return raw / Math.pow(10, selectedAsset.token.decimals);
    }, [walletBalances, selectedAsset]);

    const insufficientBalance = availableBalance !== null && Number(amount) > availableBalance;

    const handleFund = async () => {
        if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
            showToast('Please enter a valid amount', 'error');
            return;
        }

        if (insufficientBalance) {
            showToast('Insufficient balance in your connected wallet', 'error');
            return;
        }

        const decimalParts = amount.split('.');
        if (decimalParts[1] && decimalParts[1].length > maxDecimals) {
            showToast(
                `Maximum ${maxDecimals} decimal places allowed`,
                'error',
            );
            return;
        }

        if (!walletId || !multisigAccountId || !connectedNetwork) {
            showToast('Wallet not fully connected', 'error');
            return;
        }

        setIsLoading(true);
        try {
            let txId: string | undefined;
            const isCustom = connectedNetwork?.startsWith('custom-');

            if (isCustom) {
                const confirmed = await requestSignConfirmation({
                    type: 'Fund Wallet',
                    items: [
                        { label: 'To', value: multisigAccountId },
                        { label: 'Amount', value: `${amount} ${selectedAsset.type === 'HBAR' ? 'HBAR' : (selectedAsset as any).token?.symbol || 'Token'}` },
                    ],
                });
                if (!confirmed) {
                    setIsLoading(false);
                    return;
                }
            }

            // Build strategy AFTER confirmation (key is freshly decrypted)
            const strategy = buildCustomStrategy(connectedNetwork, accountId);

            if (selectedAsset.type === 'HBAR') {
                if (strategy) {
                    const result = await sendHbarToAccount(strategy, multisigAccountId, Number(amount));
                    txId = result?.txId;
                } else {
                    if (!hashconnectConnectorInstance) throw new Error('Wallet connector not initialized');
                    const result = await hashconnectConnectorInstance.sendHbarToAccount(multisigAccountId, Number(amount), connectedNetwork, accountId);
                    txId = result?.txId;
                }
            } else {
                if (strategy) {
                    const result = await sendTokenToAccount(strategy, multisigAccountId, Number(amount), selectedAsset.token.token_id, selectedAsset.token.decimals);
                    txId = result?.txId;
                } else {
                    if (!hashconnectConnectorInstance) throw new Error('Wallet connector not initialized');
                    const result = await hashconnectConnectorInstance.sendTokenToAccount(multisigAccountId, Number(amount), selectedAsset.token.token_id, selectedAsset.token.decimals, connectedNetwork, accountId);
                    txId = result?.txId;
                }
            }

            if (txId) {
                const tokenInfo =
                    selectedAsset.type === 'TOKEN'
                        ? {
                              tokenId: selectedAsset.token.token_id,
                              tokenSymbol: selectedAsset.token.symbol,
                              tokenName: selectedAsset.token.name,
                              tokenDecimals: selectedAsset.token.decimals,
                          }
                        : undefined;

                try {
                    await fundWallet(walletId, Number(amount), txId, tokenInfo);
                } catch (backendError) {
                    console.error('Backend record failed:', backendError);
                    showToast(
                        `Transfer succeeded but failed to save record. Transaction ID: ${txId}`,
                        'warning',
                    );
                    handleClose();
                    return;
                }

                // Optimistically update the HBAR balance so the rolling
                // animation fires immediately instead of waiting for the
                // Mirror Node to ingest the transaction (3-8 s delay).
                if (selectedAsset.type === 'HBAR') {
                    updateBalance(multisigHbarBalance + Number(amount));
                } else {
                    // For token funds, trigger an immediate list refresh
                    triggerRefresh();
                }

                // Delayed refresh to reconcile with the real Mirror Node balance.
                setTimeout(() => triggerRefresh(), 5000);

                showToast('Wallet funded successfully!', 'success');
                handleClose();
            }
        } catch (error: any) {
            console.error('Funding failed:', error);
            showToast(getWalletErrorMessage(error, 'Failed to fund wallet'), 'error');
        } finally {
            clearKey();
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        closeModal(ModalType.FundWalletModal);
        setAmount('');
        setSelectedAsset({ type: 'HBAR' });
        setWalletBalances(null);
        setBalanceFetchFailed(false);
    };

    const assetLabel = selectedAsset.type === 'HBAR' ? 'HBAR' : selectedAsset.token.symbol;

    return (
        <Modal onClose={handleClose} isOpen={open} isCentered>
            <ModalOverlay />
            <ModalContent maxWidth="520px" w="full" mx={isScreenMedium ? 4 : 0} borderRadius={16}>
                <ModalHeader pt={6} px={6} pb={0}>
                    <Text size="lg" variant="bodyBold" textAlign="center">
                        Fund Multisig
                    </Text>
                    <ModalCloseButton
                        mt={2}
                        mr={2}
                        color="#565656"
                        _hover={{ border: 'none' }}
                        _focusVisible={{ outline: 'none' }}
                    />
                </ModalHeader>
                <ModalBody px={6} pb={6}>
                    {/* Wallet → MultiSig visual */}
                    <Flex
                        mt={4}
                        alignItems="center"
                        justifyContent="center"
                        gap={4}
                    >
                        {/* Connected wallet */}
                        <Flex
                            flex={1}
                            direction="column"
                            alignItems="center"
                            p={3}
                            bg="gray.50"
                            borderRadius={12}
                            gap={1}
                        >
                            <Box
                                w={10}
                                h={10}
                                borderRadius="full"
                                bg="white"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                boxShadow="sm"
                                overflow="hidden"
                            >
                                {connectedNetwork?.startsWith('custom-') ? (
                                    <GreenWalletIcon style={{ width: 40, height: 40 }} />
                                ) : walletMeta?.icon ? (
                                    <img
                                        src={walletMeta.icon}
                                        alt={walletMeta.name}
                                        width={40}
                                        height={40}
                                        referrerPolicy="no-referrer"
                                        style={{ objectFit: 'cover' }}
                                    />
                                ) : (
                                    <WalletConnectLogo style={{ width: 22, height: 22 }} />
                                )}
                            </Box>
                            <Text size="xs" fontWeight="600" mt={1}>
                                {walletMeta?.name || 'Your Wallet'}
                            </Text>
                            <Text fontSize="11px" color="blackAlpha.600" wordBreak="break-all" textAlign="center">
                                {accountId}
                            </Text>
                            <Skeleton isLoaded={connectedWalletDisplayBalance !== null || balanceFetchFailed} borderRadius={4} minH="16px" minW="40px">
                                <Text fontSize="12px" fontWeight="600" color={balanceFetchFailed ? 'blackAlpha.400' : 'blackAlpha.700'}>
                                    {balanceFetchFailed ? '—' : `${connectedWalletDisplayBalance} ${assetLabel}`}
                                </Text>
                            </Skeleton>
                        </Flex>

                        {/* Arrow */}
                        <Text fontSize="2xl" fontWeight="700" color="green.400" flexShrink={0}>
                            →
                        </Text>

                        {/* MultiSig */}
                        <Flex
                            flex={1}
                            direction="column"
                            alignItems="center"
                            p={3}
                            bg="gray.50"
                            borderRadius={12}
                            gap={1}
                        >
                            <HashBasedAvatar UID={getAvatarUID(walletId)} size={40} />
                            <Text size="xs" fontWeight="600" mt={1} noOfLines={1}>
                                {currentAccount.name || 'MultiSig'}
                            </Text>
                            <Text fontSize="11px" color="blackAlpha.600" wordBreak="break-all" textAlign="center">
                                {multisigAccountId}
                            </Text>
                            <Text fontSize="12px" fontWeight="600" color="blackAlpha.700">
                                {multisigDisplayBalance} {assetLabel}
                            </Text>
                        </Flex>
                    </Flex>

                    {/* Token selector */}
                    <Box mt={5}>
                        <Text size="xs" fontWeight="600" mb={1}>Asset</Text>
                        <ChakraMenu placement="bottom-start" matchWidth>
                            <MenuButton
                                as={Box}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                h={10}
                                px={3}
                                borderRadius={8}
                                border="1px solid"
                                borderColor="inherit"
                                cursor="pointer"
                                bg="white"
                                transition="all 0.15s"
                                _hover={{ borderColor: 'blackAlpha.400' }}
                            >
                                <Flex alignItems="center" gap={2}>
                                    <TokenIcon
                                        symbol={selectedAsset.type === 'HBAR' ? 'HBAR' : selectedAsset.token.symbol}
                                        size={20}
                                        tokenId={selectedAsset.type === 'HBAR' ? 'HBAR' : selectedAsset.token.token_id}
                                    />
                                    <Text fontSize="sm" fontWeight="500">
                                        {selectedAsset.type === 'HBAR' ? 'HBAR' : `${selectedAsset.token.symbol} - ${selectedAsset.token.name}`}
                                    </Text>
                                </Flex>
                            </MenuButton>
                            <MenuList py={2} borderRadius={12} boxShadow="lg" zIndex={10} maxH="240px" overflowY="auto">
                                <MenuItem
                                    fontSize="sm"
                                    py={2}
                                    fontWeight={selectedAsset.type === 'HBAR' ? '600' : '400'}
                                    bg={selectedAsset.type === 'HBAR' ? 'blackAlpha.50' : 'transparent'}
                                    _hover={{ bg: 'blackAlpha.100' }}
                                    onClick={() => handleAssetChange('HBAR')}
                                >
                                    <Flex alignItems="center" gap={2}>
                                        <TokenIcon symbol="HBAR" size={20} tokenId="HBAR" />
                                        HBAR
                                    </Flex>
                                </MenuItem>
                                {associatedTokens.map((token) => (
                                    <MenuItem
                                        key={token.token_id}
                                        fontSize="sm"
                                        py={2}
                                        fontWeight={selectedAsset.type === 'TOKEN' && selectedAsset.token.token_id === token.token_id ? '600' : '400'}
                                        bg={selectedAsset.type === 'TOKEN' && selectedAsset.token.token_id === token.token_id ? 'blackAlpha.50' : 'transparent'}
                                        _hover={{ bg: 'blackAlpha.100' }}
                                        onClick={() => handleAssetChange(token.token_id)}
                                    >
                                        <Flex alignItems="center" gap={2}>
                                            <TokenIcon symbol={token.symbol} size={20} tokenId={token.token_id} />
                                            {token.symbol} - {token.name}
                                        </Flex>
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </ChakraMenu>
                    </Box>

                    {/* Amount input */}
                    <Box mt={4}>
                        <Text size="xs" fontWeight="600" mb={1}>Amount</Text>
                        <Input
                            placeholder={`Enter ${assetLabel} amount`}
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            borderRadius={8}
                        />
                    </Box>

                    {/* Info banner */}
                    <Box p={3} bg="primary" borderRadius={10} mt={4} display="flex" gap={2} alignItems="flex-start">
                        <Box mt="2px" flexShrink={0}>
                            <InfoIcon />
                        </Box>
                        <Text size="xs" color="gray.600">
                            This transfer is sent immediately from your connected wallet and does not require multisig approval.
                        </Text>
                    </Box>

                    <Button
                        onClick={handleFund}
                        isLoading={isLoading}
                        isDisabled={!amount || Number(amount) <= 0 || insufficientBalance}
                        mt={5}
                        w="full"
                        variant="primary"
                        borderRadius={6}
                    >
                        {insufficientBalance
                            ? 'Insufficient balance'
                            : `Send ${amount ? `${amount} ${assetLabel}` : assetLabel}`}
                    </Button>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default FundWalletModal;